import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import FormControlLabel from '@mui/material/FormControlLabel';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import Switch from '@mui/material/Switch';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { apiUrl, fetchJson } from '../../apiBase';
import { clearSchoolInfoCache } from '../api/schoolInfo';
import {
  FEATURE_VISIBILITY_GROUPS,
  updateFeatureVisibility,
} from '../featureVisibility';

const DEFAULT_INFO = {
  code: '',
  name: '',
  title: '',
  phone: '',
  wa_phone_id: '',
  time_zone: '',
  settings: {},
};

export default function SchoolInfoManager({ schoolId, onError, onSuccess, onSaved }) {
  const [info, setInfo] = useState(DEFAULT_INFO);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const getAuthHeader = () => {
    try {
      const token = JSON.parse(localStorage.getItem('adminUser') || '{}')?.token;
      return token ? { Authorization: `Bearer ${token}` } : {};
    } catch {
      return {};
    }
  };

  const load = useCallback(async () => {
    if (!schoolId) {
      setInfo(DEFAULT_INFO);
      return;
    }

    setLoading(true);
    try {
      const res = await fetchJson(apiUrl(`/admin/school-info/${encodeURIComponent(schoolId)}`), {
        headers: getAuthHeader(),
      });
      if (!res.ok) {
        throw new Error('load');
      }
      const data = await res.json();
      setInfo({
        ...DEFAULT_INFO,
        ...(data || {}),
        settings: data?.settings || {},
      });
    } catch {
      onError && onError('Okul bilgileri yüklenemedi');
    } finally {
      setLoading(false);
    }
  }, [onError, schoolId]);

  useEffect(() => {
    load();
  }, [load]);

  const featureVisibility = useMemo(() => info?.settings?.ui?.feature_visibility || {}, [info]);

  const updateField = useCallback((field, value) => {
    setInfo((prev) => ({ ...prev, [field]: value }));
  }, []);

  const handleFeatureToggle = useCallback((featureKey, role) => (event) => {
    const checked = Boolean(event?.target?.checked);
    setInfo((prev) => ({
      ...prev,
      settings: updateFeatureVisibility(prev?.settings || {}, featureKey, role, checked),
    }));
  }, []);

  const save = useCallback(async () => {
    if (!schoolId) {
      return;
    }
    setSaving(true);
    try {
      const payload = {
        code: info.code || '',
        name: info.name || '',
        title: info.title || '',
        phone: info.phone || '',
        wa_phone_id: info.wa_phone_id || '',
        time_zone: info.time_zone || '',
        settings: info.settings || {},
      };
      const res = await fetchJson(apiUrl(`/admin/school-info/${encodeURIComponent(schoolId)}`), {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeader(),
        },
        body: JSON.stringify(payload),
      });
      if (res.status === 401) {
        onError && onError('Yetki gerekli. Lütfen yönetici olarak giriş yapın.');
        return;
      }
      if (!res.ok) {
        throw new Error('save');
      }
      const data = await res.json();
      const nextInfo = {
        ...DEFAULT_INFO,
        ...(data || {}),
        settings: data?.settings || {},
      };
      clearSchoolInfoCache(schoolId);
      setInfo(nextInfo);
      onSaved && onSaved(nextInfo);
      onSuccess && onSuccess('Okul bilgileri güncellendi');
    } catch {
      onError && onError('Okul bilgileri güncellenemedi');
    } finally {
      setSaving(false);
    }
  }, [info, onError, onSaved, onSuccess, schoolId]);

  return (
    <Paper sx={{ p: 2, borderRadius: 2 }}>
      <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
        Okul Bilgileri
      </Typography>
      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <TextField
            label="Okul Kodu"
            value={info.code || ''}
            onChange={(event) => updateField('code', event.target.value)}
            fullWidth
            size="small"
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            label="Okul Adı"
            value={info.name || ''}
            onChange={(event) => updateField('name', event.target.value)}
            fullWidth
            size="small"
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            label="Başlık"
            value={info.title || ''}
            onChange={(event) => updateField('title', event.target.value)}
            fullWidth
            size="small"
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            label="Telefon"
            value={info.phone || ''}
            onChange={(event) => updateField('phone', event.target.value)}
            fullWidth
            size="small"
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            label="WhatsApp Phone ID"
            value={info.wa_phone_id || ''}
            onChange={(event) => updateField('wa_phone_id', event.target.value)}
            fullWidth
            size="small"
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            label="Saat Dilimi"
            value={info.time_zone || ''}
            onChange={(event) => updateField('time_zone', event.target.value)}
            fullWidth
            size="small"
          />
        </Grid>
      </Grid>

      <Divider sx={{ my: 3 }} />

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, gap: 2, flexWrap: 'wrap' }}>
        <Box>
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            Özellik Görünürlüğü
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Yalnızca arayüz görünümünü etkiler. Yetkilendirme kuralları backend tarafında değişmez.
          </Typography>
        </Box>
      </Box>

      <Box sx={{ display: 'grid', gap: 2 }}>
        {FEATURE_VISIBILITY_GROUPS.map((group) => (
          <Paper key={group.key} variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5 }}>
              {group.label}
            </Typography>
            <Grid container spacing={1}>
              {group.items.map((item) => {
                const config = featureVisibility[item.key] || {};
                const adminChecked = typeof config.admin === 'boolean' ? config.admin : true;
                const teacherChecked = typeof config.teacher === 'boolean' ? config.teacher : true;
                return (
                  <Grid item xs={12} md={6} key={item.key}>
                    <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, p: 1.5 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                        {item.label}
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                        <FormControlLabel
                          control={<Switch checked={adminChecked} onChange={handleFeatureToggle(item.key, 'admin')} />}
                          label="Admin"
                        />
                        <FormControlLabel
                          control={<Switch checked={teacherChecked} onChange={handleFeatureToggle(item.key, 'teacher')} />}
                          label="Öğretmen"
                        />
                      </Box>
                    </Box>
                  </Grid>
                );
              })}
            </Grid>
          </Paper>
        ))}
      </Box>

      <Box sx={{ mt: 2, display: 'flex', gap: 1 }}>
        <Button variant="contained" onClick={save} disabled={loading || saving}>
          Kaydet
        </Button>
        <Button variant="outlined" onClick={load} disabled={loading || saving}>
          Yenile
        </Button>
      </Box>
    </Paper>
  );
}